import {
  getReconciliations,
  getTransactions,
} from '../api/requests';
import {
  DATA_PERIODS,
  PAYMENT_MODES,
  RECONCILIATION_STAGES,
  TRANSACTION_TYPES,
} from '../resources/enums';
import { useEffect, useState } from 'react';
import { Account } from '../components/models/account/Account';
import useModal from './modalHook';
import AddTransaction from '../views/transactions/AddTransaction';
import { ITransaction, Transaction } from '../components/models/transaction/Transaction';
import { getPaginationDataFromResponse, getSelectItemsFromEnum } from '../utilities/miscFunctions';
import { ISelectDropdownItem } from '../components/form-control/SelectDropdown';
import { ITablePaginationData } from '../components/elements/GTable/GTable';
import { Reconciliation } from '../resources/types';
import usePayments from './PaymentsHook';
import { IPaymentData } from '../resources/interfaces';

interface ITransactionHook {
  onTransactionAdded?: (transaction?: Transaction) => void;
}

function useTransactions(props: ITransactionHook) {
  const { ModalComponent, updateModalStates, closeModal, modalState } = useModal();
  const [loadingTransactions, setLoadingTransactions] = useState<boolean>(true);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [refreshing, setRefreshing] = useState<boolean>(true);
  const { handlePayment } = usePayments({ onPaymentProcessed: () => onPaymentProcessed() });

  const TModalComponent = ModalComponent;

  const [paginationData, setPaginationData] = useState<ITablePaginationData>({
    total: 0,
    per_page: 0,
    total_pages: 0,
    current_page: 1,
    to: 0,
    from: 0
  });

  const TransactionTypesList = getSelectItemsFromEnum(TRANSACTION_TYPES).map((type: ISelectDropdownItem) => (
    {
      name: type.name === "Credit" ? "Deposit" : "Withdrawal",
      value: type.value,
      id: type.id
    }
  ))

  const [reconciliations, setReconciliations] = useState<Reconciliation[]>([]);

  const addTransaction = (type?: TRANSACTION_TYPES, account?: Account, shouldReset?: boolean, initiatePayment?: boolean) => {
    updateModalStates({
      show: true,
      children: <AddTransaction
        account={account}
        type={type}
        onComplete={(transaction?: Transaction) => {
          props.onTransactionAdded && props.onTransactionAdded(transaction);

          if (!shouldReset) {
            closeModal();
          }
        }}
        initiatePayment={initiatePayment}
        shouldReset={shouldReset}
      />,
      title: "Add Transaction",
      static: true,
    })
  }

  const onPaymentProcessed = () => {
    // refetch();
  }

  const fetchTransactions = ({ query, limit = 25, type, agentId, from, to, mode, page, branchId }: {
    query?: string,
    limit?: number,
    type?: TRANSACTION_TYPES,
    agentId?: string,
    from?: string,
    to?: string,
    mode?: PAYMENT_MODES,
    page?: number,
    branchId?: string
  }) => {
    getTransactions(query, limit, type, agentId, from, to, mode, page, branchId)
      .then((response: any) => {
        const _transactions = response?.data?.map((ITransaction: ITransaction) => new Transaction(ITransaction));
        setTransactions(_transactions);

        setPaginationData(getPaginationDataFromResponse(response));
      }).catch((error: any) => {
        console.log("request error", error);
      }).finally(() => {
        setLoadingTransactions(false);
        setRefreshing(false);
      })
  }

  const fetchReconciliations = ({ query, limit = 25, status, agentId, from, to, page }: {
    query?: string,
    limit?: number,
    status?: RECONCILIATION_STAGES,
    agentId?: string,
    from?: string,
    to?: string,
    page?: number
  }) => {
    getReconciliations(query, limit, status, agentId, from, to)
      .then((response: any) => {
        setReconciliations(response.data);
      }).catch((error: any) => {
        console.log("request error", error);
      }).finally(() => {
        setLoadingTransactions(false);
        setRefreshing(false);
      })
  }

  return {
    addTransaction,
    TModalComponent,
    transactions,
    loadingTransactions,
    setTransactions,
    fetchTransactions,
    refreshing,
    TransactionTypesList,
    paginationData,
    fetchReconciliations,
    reconciliations
  };
}

export default useTransactions;
